/**
 * Structured data utility for JSON-LD schema.org markup
 * Provides functions to generate structured data for SEO
 */

import { BASE_URL, CONTACT_EMAIL, SOCIAL_HANDLES } from './constants'
import type {
  Article,
  BreadcrumbList,
  FAQPage,
  ItemList,
  Organization,
  SoftwareApplication,
  WebSite,
  WithContext,
} from 'schema-dts'
import { totalModelCount } from '@/features/home/data/homeContent'

/**
 * Organization schema for the company
 * Used on homepage and other key pages
 */
export function getOrganizationSchema(): WithContext<Organization> {
  const sameAs: Array<string> = []

  if (SOCIAL_HANDLES.twitter) {
    sameAs.push(`https://twitter.com/${SOCIAL_HANDLES.twitter}`)
  }
  if (SOCIAL_HANDLES.github) {
    sameAs.push(`https://github.com/${SOCIAL_HANDLES.github}`)
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ModelStack',
    legalName: 'ModelStack Inc.',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description:
      `Unified access to ${totalModelCount}+ leading AI models including Claude, GPT, Gemini, and more through a single API gateway.`,
    foundingDate: '2026',
    sameAs,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: CONTACT_EMAIL,
      availableLanguage: ['English'],
    },
  }
}

/**
 * WebSite schema with search action
 * Used on homepage
 */
export function getWebSiteSchema(): WithContext<WebSite> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ModelStack',
    url: BASE_URL,
    description: `Unified API gateway for ${totalModelCount}+ leading AI models`,
  }
}

/**
 * SoftwareApplication schema for the API platform
 * Used on homepage and models page
 */
export function getSoftwareApplicationSchema(): WithContext<SoftwareApplication> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'ModelStack API',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Pay-as-you-go pricing starting from $0',
    },
    // No aggregateRating here — Google's structured-data policy requires
    // review/rating markup to be backed by real, verifiable reviews, and
    // this page has none (an earlier hardcoded 4.8/150 was fabricated).
    description:
      `Unified API gateway providing access to ${totalModelCount}+ leading AI models including Claude, GPT, Gemini, Kimi, QWen, and more.`,
    featureList: [
      `Access to ${totalModelCount}+ AI models`,
      'Single API key for all models',
      'OpenAI-compatible API',
      'Sub-15ms latency overhead',
      'Zero-log policy',
      'Unified billing',
    ],
    softwareVersion: '1.0',
    applicationSubCategory: 'API Gateway',
    author: {
      '@type': 'Organization',
      name: 'ModelStack',
    },
  }
}

/**
 * FAQ schema for pricing page
 * @param faqs Array of FAQ items with question and answer
 */
export function getFAQSchema(
  faqs: Array<{ question: string; answer: string }>,
): WithContext<FAQPage> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

/**
 * BreadcrumbList schema for navigation
 * @param items Array of breadcrumb items with name and url
 */
export function getBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
): WithContext<BreadcrumbList> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  }
}

/**
 * ItemList schema for the /models catalog page.
 *
 * Intentionally lightweight: one SoftwareApplication entry per rendered model
 * with just its display name, model ID and provider. No pricing, ratings or
 * capability claims are emitted here — those would duplicate (and risk
 * drifting from) the per-model detail the page already renders.
 *
 * @param models The models actually rendered on the page, in display order
 */
export function getModelsItemListSchema(
  models: Array<{ id: string; name: string; provider: string }>,
): WithContext<ItemList> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'AI models available on ModelStack',
    description:
      'AI models accessible through the ModelStack unified API gateway.',
    url: `${BASE_URL}/models`,
    numberOfItems: models.length,
    itemListElement: models.map((model, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'SoftwareApplication',
        name: model.name,
        identifier: model.id,
        applicationCategory: 'DeveloperApplication',
        provider: {
          '@type': 'Organization',
          name: model.provider,
        },
      },
    })),
  }
}

/**
 * Article schema for blog posts.
 * Single source of truth so every post gets the same shape (and the same
 * `</script` escaping via renderJsonLd) instead of each route hand-rolling it.
 */
export function getArticleSchema(post: {
  title: string
  description: string
  slug: string
  coverImage?: string | null
  publishedAt?: string | null
  createdAt: string
  updatedAt: string
  tags: Array<string>
}): WithContext<Article> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    image: post.coverImage || `${BASE_URL}/og-image.png`,
    author: { '@type': 'Organization', name: 'ModelStack' },
    publisher: {
      '@type': 'Organization',
      name: 'ModelStack',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.png` },
    },
    datePublished: post.publishedAt || post.createdAt,
    dateModified: post.updatedAt,
    mainEntityOfPage: `${BASE_URL}/blogs/${post.slug}`,
    keywords: post.tags.join(', '),
  }
}

/**
 * Offer schema for a model detail page. Deliberately cites only the standard
 * per-unit rate already shown on the page (not economy/premium tier math) so
 * the schema can never drift from — or overstate — what's actually rendered.
 */
export function getModelOfferSchema(model: {
  id: string
  name: string
  description?: string
  canonical: string
  pricing:
    | { type: 'chat'; inputPricePerMillion: number }
    | { type: 'generation'; priceMillidollars: number; priceUnit: string }
}): WithContext<SoftwareApplication> {
  const offer =
    model.pricing.type === 'chat'
      ? {
          '@type': 'Offer' as const,
          price: model.pricing.inputPricePerMillion.toFixed(6),
          priceCurrency: 'USD',
          description:
            'Standard-tier input price per 1M tokens. Output, cache, and other tier pricing shown on the page.',
        }
      : {
          '@type': 'Offer' as const,
          price: (model.pricing.priceMillidollars / 1000).toFixed(4),
          priceCurrency: 'USD',
          description: `Price per generation, billed per ${model.pricing.priceUnit}.`,
        }

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: model.name,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Any',
    url: `${BASE_URL}${model.canonical}`,
    description:
      model.description ||
      `${model.name} available through the ModelStack unified API gateway.`,
    offers: offer,
  }
}

/**
 * Helper function to render JSON-LD script tag
 * @param schema The schema object to render
 */
export function renderJsonLd(schema: WithContext<any>): string {
  // Escape `</script` so dynamic string fields (e.g. CMS-authored blog
  // titles/descriptions) can never break out of the surrounding <script> tag.
  return JSON.stringify(schema).replace(/<\/script/gi, '<\\/script')
}
